import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';

interface UseCleanupMutationOptions {
  mutationFn: () => Promise<void>;
  queryKeys: (string | undefined)[][];
  translationPrefix: string;
}

export function useCleanupMutation({ mutationFn, queryKeys, translationPrefix }: UseCleanupMutationOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  const mutation = useMutation({
    mutationFn,
    onSuccess: () => {
      queryKeys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
      toast.success(t(`${translationPrefix}.cleanupSuccess`));
    },
    onError: (error: unknown) => {
      setIsDeleting(false);
      setDeleteProgress(0);
      const message = resolveApiErrorMessage(error, t);
      toast.error(t(`${translationPrefix}.cleanupError`), { description: message });
    }
  });

  const runCleanup = async () => {
    setIsDeleting(true);
    const progressInterval = setInterval(() => {
      setDeleteProgress(prev => Math.min(prev + (Math.random() * 15), 90));
    }, 200);

    try {
      await mutation.mutateAsync();
      clearInterval(progressInterval);
      setDeleteProgress(100);
      setTimeout(() => {
        setIsDeleting(false);
        setDeleteProgress(0);
      }, 400);
    } catch {
      clearInterval(progressInterval);
    }
  };

  return { isDeleting, deleteProgress, runCleanup };
}
